import React from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import { IPatientRisk } from "../../types/noShowPredictor.types";

interface IPatientRiskCardProps {
  patient: IPatientRisk;
  onSendReminder: (patientId: number) => void;
  onCall: (patientId: number) => void;
  onAddToWaitlist: (patientId: number) => void;
}

/**
 * Patient risk card with factors and actions
 */
const PatientRiskCard: React.FC<IPatientRiskCardProps> = ({ 
  patient, 
  onSendReminder, 
  onCall, 
  onAddToWaitlist 
}) => {
  const isLowRisk = patient.riskLevel === 'low';
  
  // Day name translation keys
  const dayKeys = [
    'APPOINTMENTS_DAY_SUNDAY',
    'APPOINTMENTS_DAY_MONDAY', 
    'APPOINTMENTS_DAY_TUESDAY',
    'APPOINTMENTS_DAY_WEDNESDAY',
    'APPOINTMENTS_DAY_THURSDAY',
    'APPOINTMENTS_DAY_FRIDAY',
    'APPOINTMENTS_DAY_SATURDAY'
  ];
  
  // Format date for display with translated day name
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const dayName = t(dayKeys[date.getDay()]);
    return `${dayName}, ${date.getDate()}/${date.getMonth() + 1}`;
  };

  return (
    <div className={`${styles.patientCard} ${styles[`${patient.riskLevel}Risk`]}`}>
      <div className={styles.patientHeader}>
        <div className={styles.patientInfo}>
          <div className={styles.patientName}>👤 {patient.patientName}</div>
          <div className={styles.appointmentDetails}>
            {formatDate(patient.appointmentDate)} {t("NOSHOW_AT_TIME")} {patient.appointmentTime} • {patient.therapistName}
          </div>
        </div>
        <div className={styles.riskScore}>
          <div className={`${styles.riskPercentage} ${styles[patient.riskLevel]}`}>
            {patient.riskPercentage}%
          </div>
          <div className={`${styles.riskLabel} ${styles[patient.riskLevel]}`}>
            {t(`NOSHOW_RISK_${patient.riskLevel.toUpperCase()}`)}
          </div>
        </div>
      </div>

      {/* Risk Factors */}
      <div className={styles.riskFactors}>
        {patient.riskFactors.map(factor => (
          <div 
            key={factor.id} 
            className={`${styles.riskFactor} ${factor.isNegative ? styles.negative : styles.positive}`}
          >
            {factor.isNegative ? '❌' : '✓'} {factor.text}
          </div>
        ))}
      </div>

      {/* Prediction Details */}
      <div className={styles.predictionDetails}>
        <div className={styles.predictionRow}>
          <span className={styles.predictionLabel}>{t("NOSHOW_HISTORICAL_PATTERN")}:</span>
          <span className={styles.predictionValue}>{patient.predictionDetails.historicalPattern}</span>
        </div>
        <div className={styles.predictionRow}>
          <span className={styles.predictionLabel}>{t("NOSHOW_REMINDER_RESPONSE")}:</span>
          <span className={styles.predictionValue}>{patient.predictionDetails.reminderResponse}</span>
        </div>
        <div className={styles.predictionRow}>
          <span className={styles.predictionLabel}>{t("NOSHOW_PREVIOUS_APPOINTMENTS")}:</span>
          <span className={styles.predictionValue}>{patient.predictionDetails.previousAppointments}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.patientActions}>
        <button
          className={`${styles.patientBtn} ${styles.reminder}`}
          onClick={() => onSendReminder(patient.id)}
          disabled={isLowRisk && patient.reminderConfirmed}
        >
          {isLowRisk && patient.reminderConfirmed 
            ? `✓ ${t("NOSHOW_BTN_REMINDER_SENT")}` 
            : `📧 ${t("NOSHOW_BTN_SEND_REMINDER")}`
          }
        </button>
        <button
          className={`${styles.patientBtn} ${styles.call}`}
          onClick={() => onCall(patient.id)}
          disabled={isLowRisk}
        >
          {isLowRisk ? t("NOSHOW_BTN_NOT_REQUIRED") : `📞 ${t("NOSHOW_BTN_CALL")}`}
        </button>
        <button
          className={`${styles.patientBtn} ${styles.waitlist}`}
          onClick={() => onAddToWaitlist(patient.id)}
          disabled={isLowRisk}
        >
          {isLowRisk ? t("NOSHOW_BTN_NOT_RELEVANT") : `📋 ${t("NOSHOW_BTN_WAITLIST")}`}
        </button>
      </div>
    </div>
  );
};

export default PatientRiskCard;
